import React, { useCallback, useEffect, useState } from "react";
import { auditApi, AuditLogEntry } from "../api";
import { useAuth } from "../hooks/useAuth";

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const DEFAULT_META: PaginationMeta = {
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 0,
};

const AuditLogs: React.FC = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [meta, setMeta] = useState<PaginationMeta>(DEFAULT_META);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState({
    method: "",
    status: "",
    url: "",
    actor: "",
    role: "",
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await auditApi.getAuditLogs({
        page: meta.page,
        limit: meta.limit,
        method: appliedFilters.method || undefined,
        status: appliedFilters.status ? Number(appliedFilters.status) : undefined,
        url: appliedFilters.url || undefined,
        actor: appliedFilters.actor || undefined,
        role: appliedFilters.role || undefined,
      });

      if (response.success && response.data) {
        setLogs(response.data);
        if (response.meta) {
          setMeta((prev) => ({
            ...prev,
            page: response.meta!.page,
            limit: response.meta!.limit,
            total: response.meta!.total,
            totalPages: response.meta!.totalPages,
          }));
        }
      } else {
        setError(response.message || "Unable to fetch audit logs.");
      }
    } catch (err: any) {
      console.error("Failed to fetch audit logs", err);
      setError(err.response?.data?.message || "Failed to fetch audit logs.");
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, meta.limit, meta.page]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleApplyFilters = () => {
    setMeta((prev) => ({ ...prev, page: 1 }));
    setAppliedFilters(filters);
  };

  const handleResetFilters = () => {
    const reset = { method: "", status: "", url: "", actor: "", role: "" };
    setFilters(reset);
    setAppliedFilters(reset);
    setMeta((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (direction: "prev" | "next") => {
    setMeta((prev) => {
      const nextPage =
        direction === "prev" ? Math.max(1, prev.page - 1) : Math.min(prev.totalPages || 1, prev.page + 1);
      return { ...prev, page: nextPage };
    });
  };

  const renderStatusBadge = (statusCode: number) => {
    const isError = statusCode >= 400;
    return (
      <span
        className={`px-2 py-1 rounded text-xs font-semibold ${
          isError ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
        }`}
      >
        {statusCode}
      </span>
    );
  };

  if (user?.role !== "SUPER_ADMIN") {
    return (
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
        <h1 className="text-xl font-semibold text-gray-900">Audit Logs</h1>
        <p className="mt-4 text-gray-600">
          You do not have permission to view audit logs. Please contact a Super Admin for access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Audit Logs</h1>
          <p className="text-gray-600">Track administrative actions across the platform.</p>
        </div>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">HTTP Method</label>
            <select
              value={filters.method}
              onChange={(event) => setFilters((prev) => ({ ...prev, method: event.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All</option>
              {["GET", "POST", "PUT", "PATCH", "DELETE"].map((method) => (
                <option key={method} value={method}>
                  {method}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status Code</label>
            <input
              type="number"
              value={filters.status}
              onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
              placeholder="e.g., 200"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div className="md:col-span-1 xl:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">URL Contains</label>
            <input
              type="text"
              value={filters.url}
              onChange={(event) => setFilters((prev) => ({ ...prev, url: event.target.value }))}
              placeholder="/api/users"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Actor</label>
            <input
              type="text"
              value={filters.actor}
              onChange={(event) => setFilters((prev) => ({ ...prev, actor: event.target.value }))}
              placeholder="Name, username, or ID"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              value={filters.role}
              onChange={(event) => setFilters((prev) => ({ ...prev, role: event.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Any</option>
              <option value="SUPER_ADMIN">Super Admin</option>
              <option value="SYSTEM_ADMIN">System Admin</option>
              <option value="CONTENT_ADMIN">Content Admin</option>
              <option value="OPERATIONS_ADMIN">Operations Admin</option>
              <option value="MOBILISATION_PARTNER_ADMIN">Mobilisation Partner Admin</option>
              <option value="CENTER_ADMIN">Center Admin</option>
              <option value="PROJECT_MANAGER">Project Manager</option>
              <option value="TRAINER">Trainer</option>
              <option value="TRAINEE">Trainee</option>
            </select>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 justify-end">
          <button
            onClick={handleResetFilters}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
          >
            Reset
          </button>
          <button
            onClick={handleApplyFilters}
            className="px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700"
          >
            Apply Filters
          </button>
        </div>
      </div>

      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900">Log Entries</h2>
          <p className="text-sm text-gray-500">
            Showing {logs.length} of {meta.total} records
          </p>
        </div>

        {error && (
          <div className="px-6 py-4 bg-red-50 text-red-700 border-b border-red-200">{error}</div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  URL
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source IP
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-10 text-center text-gray-500">
                    Loading audit logs...
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-10 text-center text-gray-500">
                    No audit log entries found.
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-800">
                      {log.http_method}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 break-all">{log.url}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{renderStatusBadge(log.status_code)}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {log.actor_name || log.actor_username || log.actor_id ? (
                        <div className="space-y-1">
                          <div className="font-medium text-gray-900">
                            {log.actor_name || log.actor_username || log.actor_id}
                          </div>
                          {log.actor_username && (
                            <div className="text-xs text-gray-500">@{log.actor_username}</div>
                          )}
                          {log.actor_id && (
                            <div className="text-xs text-gray-400">ID: {log.actor_id}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 italic">Service / Anonymous</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {log.actor_role ?? <span className="text-gray-400 italic">N/A</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {log.source_ip ?? <span className="text-gray-400 italic">N/A</span>}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Page {meta.page} of {Math.max(meta.totalPages, 1)}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handlePageChange("prev")}
              disabled={meta.page <= 1 || loading}
              className="px-3 py-2 rounded border border-gray-300 text-sm text-gray-600 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange("next")}
              disabled={meta.page >= (meta.totalPages || 1) || loading}
              className="px-3 py-2 rounded border border-gray-300 text-sm text-gray-600 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogs;
